import { useState } from "react";
import axios from "axios";
import { LegalLayout, LegalSection } from "../components/LegalLayout";
import Captcha from "../components/Captcha";
import { useToast } from "../hooks/use-toast";
import { restaurantInfo } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DoNotSell() {
  const [form, setForm] = useState({ name: "", email: "", website: "" });
  const [captcha, setCaptcha] = useState({ answer: "", token: "" });
  const [captchaKey, setCaptchaKey] = useState(0);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: "Please fill in all fields", variant: "destructive" });
      return;
    }
    if (!captcha.answer || !captcha.token) {
      toast({ title: "Please solve the quick human check", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/do-not-sell`, {
        name: form.name,
        email: form.email,
        website: form.website,
        captcha_answer: captcha.answer,
        captcha_token: captcha.token,
      });
      setSent(true);
      toast({ title: "Request received", description: res.data?.message || "We'll process your request within 30 days." });
    } catch (err) {
      const status = err?.response?.status;
      let detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      if (status === 429) detail = "Too many submissions. Please try again later.";
      toast({ title: "Couldn't submit request", description: detail, variant: "destructive" });
      setCaptcha({ answer: "", token: "" });
      setCaptchaKey((k) => k + 1);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LegalLayout
      title="Do Not Sell My Personal Information"
      updated="January 2026"
      seo={{
        description: "Exercise your California CCPA right to opt out of the sale of your personal information collected by Nina's Taqueria.",
        path: "/do-not-sell",
      }}
    >
      <LegalSection heading="Your right to opt out">
        <p>
          Under the California Consumer Privacy Act (CCPA), California residents have the
          right to opt out of the sale of their personal information. Nina's Taqueria,
          Inc. does not sell personal information in the traditional sense of the word,
          but we honor all opt-out requests received through this form.
        </p>
        <p>
          To submit a request, please fill out the form below. We will process your
          request within 30 days as required by California law.
        </p>
      </LegalSection>

      <div className="bg-cream p-6 md:p-8 rounded-2xl border border-cream-100 shadow-sm mt-6">
        {sent ? (
          <div className="text-center py-4">
            <div className="font-hand text-3xl text-brand mb-2">¡Gracias!</div>
            <p className="text-granite-dark">
              Your request has been received. We'll be in touch within 30 days.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" noValidate>
            <h3 className="font-display text-2xl font-semibold text-granite-dark">
              Opt-out request
            </h3>
            <div>
              <label className="text-xs uppercase tracking-widest text-granite/60 font-semibold">
                Full name*
              </label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1.5 w-full px-4 py-3 rounded-xl bg-cream-50 border border-cream-100 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none text-sm text-granite-dark"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-granite/60 font-semibold">
                Email*
              </label>
              <input
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 w-full px-4 py-3 rounded-xl bg-cream-50 border border-cream-100 focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none text-sm text-granite-dark"
              />
            </div>

            {/* Honeypot */}
            <div className="hidden" aria-hidden="true">
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
              />
            </div>

            <Captcha value={captcha} onChange={setCaptcha} refreshKey={captchaKey} />

            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 bg-granite-dark hover:bg-brand text-cream px-6 py-3 rounded-full font-medium text-sm transition-all disabled:opacity-60"
            >
              {submitting ? "Sending…" : "Submit request"}
            </button>
          </form>
        )}
      </div>

      <LegalSection heading="Questions?">
        <p>
          You may also send your opt-out request by email to{" "}
          <a href={`mailto:${restaurantInfo.email}`} className="text-brand hover:underline">
            {restaurantInfo.email}
          </a>{" "}
          or by phone at {restaurantInfo.phone}.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
